import { Box } from '@mui/material';
import Grid from '@mui/material/Grid';
import { useMemo, useState } from 'react';
import { transformData } from '/@/components/crop-monitoring/assets/graphicData';
import Graphic from '/@/components/crop-monitoring/graphic';
import IconTitleText from '/@/components/crop-monitoring/icon-title-text';
import LineGraphic from '/@/components/line-graphic';
import colors from '/@/components/theme-provider/colors';
import useUnitContext from '/@/core/context/unit';
import useIsMobile from '/@/utils/hooks/is-mobile';
import { References } from '../../gdu-graphic';

const PageArrow = ({ direction, onClick, page, pages }) => {
    const disabled = (direction === 'left' && page === 1) || (direction === 'right' && page === pages);
    return (
        <Box
            sx={[
                {
                    '& i': {
                        mb: 0.25,
                        borderRadius: '100%',
                        background: colors.gray[800],
                        color: 'white',
                    },
                },
                !disabled && {
                    '& i': {
                        cursor: 'pointer',
                        '&:hover': {
                            background: (t) => t.palette.primary.main,
                        },
                    },
                },
                disabled && {
                    '& i': {
                        background: colors.gray[400],
                    },
                },
                direction === 'right' && {
                    mr: 1,
                },
                direction === 'left' && {
                    ml: 1,
                },
            ]}
            onClick={() => !disabled && onClick()}
        >
            <i className='material-icons'>{`chevron_${direction}`}</i>
        </Box>
    );
};

/**
 *
 * @param {{
 * references?: {text: string, color: string, dashed?: boolean}[]
 * }}
 */
const CardGraphic = ({
    historicalWeather,
    type,
    quantity,
    title,
    icon,
    showXAxis,
    showYAxis,
    domain,
    max,
    graphicType,
    text,
    min,
    references,
}) => {
    const isMobile = useIsMobile();
    const pages = Math.ceil(historicalWeather.count / quantity);

    const [page, setPage] = useState(pages);
    const units = useUnitContext();
    const filteredData = useMemo(() => {
        if (historicalWeather && quantity && pages)
            return transformData(historicalWeather, type, quantity, page, pages);
    }, [historicalWeather, type, quantity, page, pages]);

    const previousPage = () => {
        setPage((pv) => pv - 1);
    };

    const nextPage = () => {
        setPage((pv) => pv + 1);
    };

    return (
        <Grid
            container
            alignItems='center'
            justifyContent={!isMobile ? 'flex start' : 'center'}
            sx={{
                boxShadow: { md: 'rgb(100 100 111 / 20%) 0px 7px 29px 0px' },
                borderRadius: 4,
                ...(isMobile
                    ? { pt: { xs: 2, sm: 2 }, pb: { xs: 0, sm: 2 }, pl: { xs: 0, sm: 2 }, pr: { xs: 0, sm: 2 } }
                    : { p: 3 }),
                border: isMobile ? 1 : 0,
                borderColor: `${colors.gray[500]}`,
            }}
        >
            <Grid item container justifyContent='space-between' xs={12} sx={{ pt: 1, position: 'relative', gap: 2 }}>
                <Grid item xs={12} sm>
                    <IconTitleText icon={icon} title={title} text={text} card={isMobile ? false : true} />
                </Grid>
                <Grid item xs={12} sm>
                    {references && <References references={references} />}
                </Grid>
            </Grid>
            <Grid
                item
                sx={{
                    height: isMobile ? '250px' : '350px',
                    width: '100%',
                    my: 2,
                    borderRadius: 4,
                    position: 'relative',
                    mt: { md: 2 },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                    }}
                >
                    <PageArrow direction='left' onClick={() => previousPage()} page={page} pages={pages} />
                    {graphicType === 'line' ? (
                        <LineGraphic
                            dataGraphic={filteredData || []}
                            type={type}
                            width={'100%'}
                            height={isMobile ? 250 : 350}
                            showXAxis={showXAxis}
                            showYAxis={showYAxis}
                            domain={domain}
                            max={max}
                            min={min}
                        />
                    ) : (
                        <Graphic
                            dataGraphic={filteredData || []}
                            type={type}
                            width={'100%'}
                            height={isMobile ? 250 : 350}
                            showXAxis={showXAxis}
                            showYAxis={showYAxis}
                            domain={domain}
                            max={max}
                        />
                    )}

                    <PageArrow direction='right' onClick={() => nextPage()} page={page} pages={pages} />
                </Box>
            </Grid>
        </Grid>
    );
};

export default CardGraphic;
